import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';

import ModalTypes from '../../constants/ModalTypes';
import FixedContainer from '../../containers/FixedContainer';
import StaticContainer from '../../containers/StaticContainer';
import UsersModalContainer from '../../containers/UsersModalContainer';
import UserSettingsModalContainer from '../../containers/UserSettingsModalContainer';
import ProjectAddModalContainer from '../../containers/ProjectAddModalContainer';
import InviteUserModalContainer from '../../containers/InviteUserModalContainer';
import UserManagementModalContainer from '../../containers/UserManagementModalContainer';
import Background from '../Background';
// import Bot from '../Bot';

import styles from './Core.module.scss';

const Core = React.memo(
  ({
    isInitializing,
    isSocketDisconnected,
    currentModal,
    currentProject,
    currentBoard,
    onOpenUserSettings,
  }) => {
    const [t] = useTranslation();
    const location = useLocation();

    // Check for success params from Google Drive integration
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const success = params.get('success');

      if (success === 'google_drive') {
        // Clear the param to avoid reopening on refresh
        window.history.replaceState({}, '', window.location.pathname);
        // Open user settings
        onOpenUserSettings();
      }
    }, [location, onOpenUserSettings]);

    // Debug temporal para ver el estado de currentProject
    console.log('🔍 Core render:', {
      currentProject: currentProject ? currentProject.id : 'null',
      currentProjectName: currentProject?.name,
      isInitializing,
    });

    const defaultTitle = useRef(document.title);

    useEffect(() => {
      let title;
      if (currentProject) {
        title = currentProject.name;

        if (currentBoard) {
          title += ` | ${currentBoard.name}`;
        }
      } else {
        title = defaultTitle.current;
      }

      document.title = title;
    }, [currentProject, currentBoard]);

    return (
      <>
        {isInitializing ? (
          <Loader active size="massive" />
        ) : (
          <>
            {currentProject && currentProject.background && (
              <Background
                type={currentProject.background.type}
                name={currentProject.background.name}
                imageUrl={currentProject.backgroundImage && currentProject.backgroundImage.url}
              />
            )}
            <FixedContainer />
            <StaticContainer />
            {currentModal === ModalTypes.USERS && <UsersModalContainer />}
            {currentModal === ModalTypes.USER_MANAGEMENT && <UserManagementModalContainer />}
            {currentModal === ModalTypes.INVITE_USER && <InviteUserModalContainer />}
            {currentModal === ModalTypes.USER_SETTINGS && <UserSettingsModalContainer />}
            {currentModal === ModalTypes.PROJECT_ADD && <ProjectAddModalContainer />}
          </>
        )}
        {isSocketDisconnected && (
          <div className={styles.message}>
            <div className={styles.messageHeader}>{t('common.noConnectionToServer')}</div>
            <div className={styles.messageContent}>
              <Trans i18nKey="common.allChangesWillBeAutomaticallySavedAfterConnectionRestored">
                All changes will be automatically saved
                <br />
                after connection restored
              </Trans>
            </div>
          </div>
        )}
      </>
    );
  }
);

Core.propTypes = {
  isInitializing: PropTypes.bool.isRequired,
  isSocketDisconnected: PropTypes.bool.isRequired,
  currentModal: PropTypes.oneOf(Object.values(ModalTypes)),
  /* eslint-disable react/forbid-prop-types */
  currentProject: PropTypes.object,
  currentBoard: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */
  onOpenUserSettings: PropTypes.func.isRequired,
};

Core.defaultProps = {
  currentModal: undefined,
  currentProject: undefined,
  currentBoard: undefined,
};

export default Core;
